import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import {
  Component,
  CoordinateAxis,
  Distribution,
  LoadDirection,
  ManualDistributedLoad,
  ManualLoad,
  Member,
} from '@paragon/contract-library';
import {
  Direction2D,
  distanceEquals,
  distributedForceEquals,
  Line2D,
  LineSegment2D,
  Point2D,
  point2DEquals,
  stressEquals,
} from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { calculateMemberMidline } from '@paragon/three-shared';
import { naturalSortByProperty } from '@paragon/utilities';

import { LoadDirectionSelect } from './LoadDirectionSelect';
import { LoadDistribution, LoadDistributionSelect } from './LoadDistributionSelect';
import { LoadMagnitudeSettings } from './LoadMagnitudeSettings';
import { LoadTypeSelect } from './LoadTypeSelect';

interface ManualLoadPointsFieldsProps {
  readonly manualLoad: ManualLoad;
  readonly onUpdate: (updatedManualLoad: ManualLoad) => void;
  readonly component: Component;
  readonly coordinateIsOnComponent: (definitionAxis: CoordinateAxis, coordinate: number) => boolean;
  readonly submitIfInvalid?: boolean;
  readonly disabled?: boolean;
}

function isUniform(manualLoad: ManualLoad) {
  if (manualLoad.distribution !== 'Distributed') return false;

  switch (manualLoad.magnitudeUnit) {
    case 'PoundsPerLinearFoot':
      return distributedForceEquals(manualLoad.startMagnitude, manualLoad.endMagnitude);
    case 'PoundsPerSquareFoot':
      return stressEquals(manualLoad.startMagnitude, manualLoad.endMagnitude);
    default:
      throw new Error('Unhandled magnitude unit');
  }
}

function getLoadDistribution(manualLoad: ManualLoad): LoadDistribution {
  return manualLoad.distribution === 'Concentrated'
    ? 'Concentrated'
    : isUniform(manualLoad)
      ? 'Uniform'
      : 'Trapezoidal';
}

export function ManualLoadPointsFields(props: ManualLoadPointsFieldsProps) {
  const { component, disabled, manualLoad, onUpdate, coordinateIsOnComponent, submitIfInvalid } = props;

  const sortedMembers = naturalSortByProperty(component.members, (member) => member.name);
  const memberSelected = useMemo(
    () =>
      inferSelectedMember(
        Point2D.fromObject(manualLoad.start),
        manualLoad.distribution === 'Distributed' ? Point2D.fromObject(manualLoad.end) : null,
        component,
      ),
    [manualLoad, component],
  );
  function updateSelectedMember(member: Member) {
    const memberLoadLineStart = getMemberLoadLineStart(component, member);
    const memberLoadLineEnd = getMemberLoadLineEnd(component, member);

    if (manualLoad.distribution === 'Concentrated') {
      const memberLoadLine = new LineSegment2D(memberLoadLineStart, memberLoadLineEnd);
      const updatedLoad: ManualLoad = {
        ...manualLoad,
        memberType: member.memberType,
        start: memberLoadLine.getPointAt(memberLoadLine.length / 2)!.toObject(),
      };
      onUpdate(updatedLoad);
    } else {
      const updatedLoad: ManualDistributedLoad = {
        ...manualLoad,
        memberType: member.memberType,
        start: memberLoadLineStart.toObject(),
        end: memberLoadLineEnd.toObject(),
      };
      onUpdate(updatedLoad);
    }
  }

  const defaultLoadDistribution: LoadDistribution = getLoadDistribution(manualLoad);

  const [loadDistribution, setLoadDistribution] = useState<LoadDistribution>(defaultLoadDistribution);
  function updateDistribution(updatedLoadDistribution: LoadDistribution) {
    setLoadDistribution(updatedLoadDistribution);

    const newDistribution: Distribution = updatedLoadDistribution === 'Concentrated' ? 'Concentrated' : 'Distributed';
    const updatedManualLoad: ManualLoad =
      newDistribution === 'Concentrated'
        ? {
            ...manualLoad,
            distribution: 'Concentrated',
            startMagnitude: Math.max(manualLoad.startMagnitude, manualLoad.endMagnitude),
            endMagnitude: Math.max(manualLoad.startMagnitude, manualLoad.endMagnitude),
            magnitudeUnit: 'Pounds',
          }
        : {
            ...manualLoad,
            distribution: 'Distributed',
            // When switching from concentrated to distributed, try to restore the end point from the selected member
            end: memberSelected ? getMemberLoadLineEnd(component, memberSelected) : manualLoad.start,
            magnitudeUnit: manualLoad.magnitudeUnit === 'Pounds' ? 'PoundsPerSquareFoot' : manualLoad.magnitudeUnit,
            startMagnitude: Math.max(manualLoad.startMagnitude, manualLoad.endMagnitude),
            endMagnitude: Math.max(manualLoad.startMagnitude, manualLoad.endMagnitude),
          };

    onUpdate(updatedManualLoad);
  }

  // Prevent making changes that cannot trigger changes in `pointChange`
  const cannotEditXAndProduceAPointOnTheCurrentLine =
    manualLoad.distribution === 'Concentrated' && memberSelected
      ? getMemberLoadLineStart(component, memberSelected).x === getMemberLoadLineEnd(component, memberSelected).x
      : manualLoad.distribution === 'Distributed'
        ? distanceEquals(manualLoad.start.x, manualLoad.end.x)
        : false;
  const cannotEditYAndProduceAPointOnTheCurrentLine =
    manualLoad.distribution === 'Concentrated' && memberSelected
      ? getMemberLoadLineStart(component, memberSelected).y === getMemberLoadLineEnd(component, memberSelected).y
      : manualLoad.distribution === 'Distributed'
        ? distanceEquals(manualLoad.start.y, manualLoad.end.y)
        : false;

  function pointChange(xOrY: 'x' | 'y', startOrEndKey: 'start' | 'end', memberSelectValue: Member | null) {
    if (manualLoad.distribution === 'Concentrated') return pointChangeConcentratedLoad(xOrY, memberSelectValue);
    return pointChangeDistributedLoad(manualLoad, xOrY, startOrEndKey);
  }
  function pointChangeConcentratedLoad(xOrY: 'x' | 'y', memberSelectValue: Member | null) {
    return function updateManualLoadPoint(n: number) {
      const { start } = manualLoad;

      const currentLineDirection = memberSelectValue
        ? Direction2D.fromPoints(
            getMemberLoadLineStart(component, memberSelectValue),
            getMemberLoadLineEnd(component, memberSelectValue),
          )
        : null;

      if (currentLineDirection === null) {
        onUpdate({ ...manualLoad, start: { ...manualLoad.start, [xOrY]: n } });
        return;
      }

      const currentLine = new Line2D(new Point2D(start.x, start.y), currentLineDirection);
      const intersectionLine =
        xOrY === 'x' ? new Line2D(new Point2D(n, 0), Direction2D.up) : new Line2D(new Point2D(0, n), Direction2D.right);
      const newPoint = currentLine.intersectWithLine(intersectionLine);
      if (newPoint !== null) onUpdate({ ...manualLoad, start: newPoint.toObject() });
    };
  }

  function pointChangeDistributedLoad(
    manualDistributedLoad: ManualDistributedLoad,
    xOrY: 'x' | 'y',
    startOrEndKey: 'start' | 'end',
  ) {
    return function updateManualLoadPoint(n: number) {
      const { start, end } = manualDistributedLoad;

      const currentLineDirection = Direction2D.fromPoints(new Point2D(start.x, start.y), new Point2D(end.x, end.y));

      if (currentLineDirection === null) {
        onUpdate({ ...manualDistributedLoad, [startOrEndKey]: { ...manualDistributedLoad[startOrEndKey], [xOrY]: n } });
        return;
      }

      const currentLine = new Line2D(new Point2D(start.x, start.y), currentLineDirection);
      const intersectionLine =
        xOrY === 'x' ? new Line2D(new Point2D(n, 0), Direction2D.up) : new Line2D(new Point2D(0, n), Direction2D.right);
      const newPoint = currentLine.intersectWithLine(intersectionLine);
      if (newPoint !== null) onUpdate({ ...manualDistributedLoad, [startOrEndKey]: newPoint.toObject() });
    };
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <LoadDistributionSelect
          sx={{ m: 1, flex: 1 }}
          loadDistribution={loadDistribution}
          submit={updateDistribution}
          disabled={disabled}
        />
        <PropertySelectField
          sx={{ m: 1, flex: 1 }}
          property="Member"
          value={memberSelected}
          options={sortedMembers}
          getName={(member: Member) => member.name}
          getValue={(member: Member) => member.guid}
          submit={updateSelectedMember}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <LoadTypeSelect
          sx={{ m: 1, flex: 1 }}
          loadType={manualLoad.loadType}
          submit={(loadType) => onUpdate({ ...manualLoad, loadType })}
          disabled={disabled}
        />
        <LoadDirectionSelect
          sx={{ m: 1, flex: 1 }}
          loadDirection={direction2DToLoadDirection(Direction2D.fromObject(manualLoad.direction))}
          submit={(loadDirection) => onUpdate({ ...manualLoad, direction: loadDirectionToDirection2D(loadDirection) })}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <DistanceField
          sx={{ m: 1, flex: 1 }}
          fullWidth={false}
          label={`${manualLoad.distribution === 'Concentrated' ? 'Point' : 'Start Point'} X`}
          value={manualLoad.start.x}
          onSubmit={pointChange('x', 'start', memberSelected)}
          disabled={disabled || cannotEditXAndProduceAPointOnTheCurrentLine}
          validator={(value) => {
            if (!coordinateIsOnComponent('X', value)) return 'not on truss';
            if (
              manualLoad.distribution === 'Distributed' &&
              point2DEquals({ ...manualLoad.start, x: value }, manualLoad.end)
            ) {
              return 'same as end';
            }
          }}
          canSubmitWhenInvalid={submitIfInvalid}
        />
        <DistanceField
          sx={{ m: 1, flex: 1 }}
          fullWidth={false}
          label={`${manualLoad.distribution === 'Concentrated' ? 'Point' : 'Start Point'} Y`}
          value={manualLoad.start.y}
          onSubmit={pointChange('y', 'start', memberSelected)}
          disabled={disabled || cannotEditYAndProduceAPointOnTheCurrentLine}
          validator={(value) => {
            if (!coordinateIsOnComponent('Y', value)) return 'not on truss';
            if (
              manualLoad.distribution === 'Distributed' &&
              point2DEquals({ ...manualLoad.start, y: value }, manualLoad.end)
            ) {
              return 'same as end';
            }
          }}
          canSubmitWhenInvalid={submitIfInvalid}
        />
      </Box>

      {manualLoad.distribution === 'Distributed' && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <DistanceField
            sx={{ m: 1, flex: 1 }}
            fullWidth={false}
            label="End Point X"
            value={manualLoad.end.x}
            onSubmit={pointChange('x', 'end', memberSelected)}
            disabled={disabled || cannotEditXAndProduceAPointOnTheCurrentLine}
            validator={(value) => {
              if (!coordinateIsOnComponent('X', value)) return 'not on truss';
              if (point2DEquals(manualLoad.start, { ...manualLoad.end, x: value })) return 'same as start';
            }}
            canSubmitWhenInvalid={submitIfInvalid}
          />
          <DistanceField
            sx={{ m: 1, flex: 1 }}
            fullWidth={false}
            label="End Point Y"
            value={manualLoad.end.y}
            onSubmit={pointChange('y', 'end', memberSelected)}
            disabled={disabled || cannotEditYAndProduceAPointOnTheCurrentLine}
            validator={(value) => {
              if (!coordinateIsOnComponent('Y', value)) return 'not on truss';
              if (point2DEquals(manualLoad.start, { ...manualLoad.end, y: value })) return 'same as start';
            }}
            canSubmitWhenInvalid={submitIfInvalid}
          />
        </Box>
      )}
      <LoadMagnitudeSettings
        loadDistribution={loadDistribution}
        startOrOnlyMagnitude={manualLoad.startMagnitude}
        endMagnitude={manualLoad.endMagnitude}
        magnitudeUnit={manualLoad.magnitudeUnit}
        component={component}
        submitStartOrOnlyMagnitude={(startOrOnlyMagnitude) =>
          onUpdate({
            ...manualLoad,
            startMagnitude: startOrOnlyMagnitude,
            endMagnitude: loadDistribution === 'Trapezoidal' ? manualLoad.endMagnitude : startOrOnlyMagnitude,
          })
        }
        submitEndMagnitude={(endMagnitude) => onUpdate({ ...manualLoad, endMagnitude })}
        submitMagnitudeUnit={(magnitudeUnit) => {
          if (manualLoad.distribution === 'Concentrated' && magnitudeUnit === 'Pounds') {
            onUpdate({ ...manualLoad, magnitudeUnit });
          } else if (
            manualLoad.distribution === 'Distributed' &&
            (magnitudeUnit === 'PoundsPerLinearFoot' || magnitudeUnit === 'PoundsPerSquareFoot')
          ) {
            onUpdate({ ...manualLoad, magnitudeUnit });
          } else {
            // TODO there is probably a better way to make TypeScript happy than these conditional blocks
            throw new Error('Illegal magnitude unit for manual load type. This should not happen.');
          }
        }}
        canSubmitWhenInvalid={submitIfInvalid}
        disabled={disabled}
      />

      <PropertyTextInput
        sx={{ m: 1 }}
        property="Manual Load Name (Optional)"
        value={manualLoad.name ?? ''}
        submit={(name) => onUpdate({ ...manualLoad, name })}
        disabled={disabled}
      />
    </>
  );
}

export function getMemberLoadLineStart(component: Component, member: Member) {
  const midline = member && calculateMemberMidline(member, component);
  if (!midline) {
    throw new Error('Midline not found in geometry cache');
  }

  return midline.start.x < midline.end.x
    ? new Point2D(midline.start.x, midline.start.y)
    : new Point2D(midline.end.x, midline.end.y);
}

export function getMemberLoadLineEnd(component: Component, member: Member) {
  const midline = member && calculateMemberMidline(member, component);
  if (!midline) {
    throw new Error('Midline not found in geometry cache');
  }

  return midline.start.x < midline.end.x
    ? new Point2D(midline.end.x, midline.end.y)
    : new Point2D(midline.start.x, midline.start.y);
}

export function loadDirectionToDirection2D(loadDirection: LoadDirection): Direction2D {
  switch (loadDirection) {
    case 'GlobalUp':
      return new Direction2D(0, 1);
    case 'GlobalDown':
      return new Direction2D(0, -1);
    case 'GlobalLeft':
      return new Direction2D(-1, 0);
    case 'GlobalRight':
      return new Direction2D(1, 0);
    default:
      return new Direction2D(0, -1);
  }
}

export function direction2DToLoadDirection(direction: Direction2D): LoadDirection | null {
  if (direction.x === 0 && direction.y === 1) return 'GlobalUp';
  if (direction.x === 0 && direction.y === -1) return 'GlobalDown';
  if (direction.x === 1 && direction.y === 0) return 'GlobalRight';
  if (direction.x === -1 && direction.y === 0) return 'GlobalLeft';
  return null;
}

function inferSelectedMember(startPoint: Point2D, endPoint: Point2D | null, component: Component): Member | null {
  if (!endPoint || startPoint.equals(endPoint)) {
    for (const member of component.members) {
      const memberStartPoint = getMemberLoadLineStart(component, member);
      const memberEndPoint = getMemberLoadLineEnd(component, member);
      const memberLine = new LineSegment2D(memberStartPoint, memberEndPoint).toLine();
      if (memberLine.containsPoint(startPoint)) {
        return member;
      }
    }
    return null;
  }

  const loadLine = new LineSegment2D(startPoint, endPoint);
  for (const member of component.members) {
    const memberStartPoint = getMemberLoadLineStart(component, member);
    const memberEndPoint = getMemberLoadLineEnd(component, member);
    const memberLine = new LineSegment2D(memberStartPoint, memberEndPoint).toLine();
    if (memberLine.direction.isParallelTo(loadLine.direction) && memberLine.containsSegment(loadLine)) {
      return member;
    }
  }

  return null;
}
